// src/utils/tokenVerification.js

// Constants
const TOKEN_REFRESH_THRESHOLD = 30 * 60; // 30 minutes in seconds
const INACTIVITY_TIMEOUT = 60 * 60; // 60 minutes in seconds

/**
 * Refresh token by calling the server.
 * @param {Function} handleLogout - Function to handle logout.
 */
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = () => {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
};

export const refreshToken = async (handleLogout) => {
  if (isRefreshing) {
    // Return a promise that resolves once the token is refreshed
    return subscribeTokenRefresh();
  }

  isRefreshing = true;

  try {
    const apiUrl = process.env.REACT_APP_GOZEEK_API_URL;
    const response = await fetch(`${apiUrl}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      },
      credentials: 'include',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`[refreshToken] Failed to refresh token. Status: ${response.status}`);
    }

    const data = await response.json();
    onRefreshed(data.token); // Notify all subscribers
  } catch (error) {
    console.error('[refreshToken] Error refreshing token:', error);
    handleLogout();
  } finally {
    isRefreshing = false;
  }
};

/**
 * Validate the token by calling the server.
 * @param {Function} handleLogout - Function to handle logout.
 * @param {Function} showSessionExpiredPopup - Function to show session expired popup.
 */
export const validateToken = async (handleLogout, showSessionExpiredPopup) => {
  // console.log('[validateToken] Validating token...');

  try {
    const apiUrl = process.env.REACT_APP_GOZEEK_API_URL;
    const response = await fetch(`${apiUrl}/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      cache: 'no-store'
    });

    // const currentTime = Math.floor(Date.now() / 1000);
    // const currentDate = new Date(currentTime * 1000);
    // console.log(`[validateToken] Current Time (Local): ${currentDate.toLocaleString()}`);

    if (!response.ok) {
      console.warn('[validateToken] Token validation failed.');
      handleLogout();
      throw new Error('Token validation failed');
    }

    // const data = await response.json();
    // console.log('[validateToken] Token is valid:', data);

    // if (data.newExp) {
    // const expirationDate = new Date(data.newExp * 1000);
    // console.log(`[validateToken] Token Expiry Time (Local): ${expirationDate.toLocaleString()}`);
    // }
  } catch (error) {
    console.error('[validateToken] Error validating token:', error);
    handleLogout();
    throw error;
  }
};

/**
 * Sets up an inactivity handler that logs out and refreshes the token
 * when user activity is detected (click or keypress).
 * @param {Function} handleLogout - Function to handle logout.
 * @param {Function} showSessionExpiredPopup - Function to show session expired popup.
 * @returns {Function} Cleanup function.
 */
export const setupInactivityHandler = (handleLogout, showSessionExpiredPopup) => {
  // console.log('[setupInactivityHandler] Inactivity handler set up.');

  let lastActivityTime = Math.floor(Date.now() / 1000);
  let lastRefreshTime = Math.floor(Date.now() / 1000); // Track last refresh time
  let inactivityTimerId = null;


  // Debounce function
  let debounceTimeout;
  const debounce = (func, delay) => {
    return (...args) => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => func(...args), delay);
    };
  };

  /**
   * Resets the inactivity timer and refreshes the token if needed.
   */
  const resetInactivityTimerAndRefreshToken = async () => {
    const currentTime = Math.floor(Date.now() / 1000);
    const timeSinceLastRefresh = currentTime - lastRefreshTime;

    lastActivityTime = currentTime;
    // const activityDate = new Date(lastActivityTime * 1000);
    // console.log('[setupInactivityHandler] Activity detected. Resetting timer at:', activityDate.toLocaleString());

    // Refresh token only if 30 minutes have passed since the last refresh
    if (timeSinceLastRefresh > TOKEN_REFRESH_THRESHOLD) {
      // console.log('[setupInactivityHandler] 30 minutes passed since last refresh. Refreshing token...');
      try {
        await refreshToken(handleLogout);
        lastRefreshTime = Math.floor(Date.now() / 1000); // Update last refresh time
      } catch (error) {
        console.error('[setupInactivityHandler] Error refreshing token on activity:', error);
        handleLogout();
      }
    } else {
      // console.log(`[setupInactivityHandler] Only ${timeSinceLastRefresh} seconds passed since last refresh. Skipping refresh.`);
    }
  };

  /**
   * Checks if the user has been inactive for too long.
   */
  const checkInactivity = () => {
    const currentTime = Math.floor(Date.now() / 1000);
    const inactivityDuration = currentTime - lastActivityTime;
    // const currentDate = new Date(currentTime * 1000);
    // console.log(`[setupInactivityHandler] Checking inactivity at ${currentDate.toLocaleString()}. Inactive for ${inactivityDuration} seconds.`);

    if (inactivityDuration > INACTIVITY_TIMEOUT) {
      console.warn('[setupInactivityHandler] Inactivity threshold exceeded. Initiating logout...');
      showSessionExpiredPopup();
      handleLogout();
    }
  };

  // Debounced event listener
  const debouncedResetInactivity = debounce(resetInactivityTimerAndRefreshToken, 300);

  // Listen to various user interactions
  document.addEventListener('click', debouncedResetInactivity);
  document.addEventListener('keypress', debouncedResetInactivity);

  // Check inactivity every minute
  inactivityTimerId = setInterval(checkInactivity, 60 * 1000);
  // console.log('[setupInactivityHandler] Inactivity check interval set to 60 seconds.');

  // Cleanup function to remove event listeners and clear interval
  const cleanup = () => {
    clearInterval(inactivityTimerId);
    document.removeEventListener('click', debouncedResetInactivity);
    document.removeEventListener('keypress', debouncedResetInactivity);
    // console.log('[setupInactivityHandler] Inactivity handler removed.');
  };

  return cleanup;
};